<script setup lang="ts">
import { AlertTriangle, ArrowRight } from "@lucide/vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog/index";
import { Button } from "@/components/ui/button/index";
import { ScrollArea } from "@/components/ui/scroll-area/index";

import type { ConflictInfo } from "../composables/useEditorRealTime";

interface Props {
  open: boolean;
  conflict: ConflictInfo | null;
  translationPrefix?: string | null;
}

const props = withDefaults(defineProps<Props>(), {
  translationPrefix: null,
});

const emit = defineEmits<{
  "update:open": [value: boolean];
  "keep-mine": [];
  "accept-theirs": [serverData: Record<string, unknown>];
  refresh: [];
}>();

const { t, te } = useI18n();

/**
 * Translate a field name using the model's translation prefix if available.
 */
function translateField(fieldKey: string): string {
  if (props.translationPrefix) {
    const key = `${props.translationPrefix}.fields.${fieldKey}`;
    if (te(key)) {
      return t(key);
    }
  }
  // Fallback: humanize the field key
  return fieldKey
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/^./, (str) => str.toUpperCase());
}

/**
 * Format a value for display in the conflict comparison.
 */
function formatValue(value: unknown): string {
  if (value === null || value === undefined) {
    return t("crud.realtime.value_empty", "—");
  }
  if (typeof value === "boolean") {
    return value ? t("crud.realtime.value_true", "Yes") : t("crud.realtime.value_false", "No");
  }
  if (typeof value === "object") {
    return JSON.stringify(value);
  }
  const str = String(value);
  // Truncate long values
  return str.length > 50 ? str.substring(0, 47) + "..." : str;
}

/** Actor name or fallback */
const actorDisplayName = computed(() => {
  return props.conflict?.actorName || t("crud.realtime.another_user", "Another user");
});

/** Build field change details for display */
const fieldChanges = computed(() => {
  if (!props.conflict) return [];

  return props.conflict.overlappingFields.map((fieldKey) => ({
    key: fieldKey,
    label: translateField(fieldKey),
    oldValue: props.conflict!.oldData[fieldKey],
    newValue: props.conflict!.serverData[fieldKey],
  }));
});

function handleKeepMine(): void {
  emit("keep-mine");
  emit("update:open", false);
}

function handleAcceptTheirs(): void {
  // Capture serverData BEFORE emitting update:open, which may trigger dismissConflict
  const serverData = props.conflict?.serverData ?? {};
  emit("accept-theirs", serverData);
  emit("update:open", false);
}

function handleRefresh(): void {
  emit("refresh");
  emit("update:open", false);
}
</script>

<template>
  <AlertDialog :open="open" @update:open="emit('update:open', $event)">
    <AlertDialogContent class="max-w-lg">
      <AlertDialogHeader class="text-center sm:text-center">
        <div
          class="mx-auto mb-2 flex size-12 items-center justify-center rounded-full bg-amber-500/10"
        >
          <AlertTriangle class="size-6 text-amber-500" />
        </div>
        <AlertDialogTitle class="text-xl">
          {{ t("crud.realtime.conflict_title") }}
        </AlertDialogTitle>
        <AlertDialogDescription class="text-balance">
          {{ t("crud.realtime.conflict_description_with_user", { user: actorDisplayName }) }}
        </AlertDialogDescription>
      </AlertDialogHeader>

      <!-- Field Changes Comparison -->
      <ScrollArea v-if="fieldChanges.length > 0" class="max-h-60 [&>[data-slot=scroll-area-viewport]]:max-h-60">
        <div class="space-y-3 py-2">
          <div
            v-for="change in fieldChanges"
            :key="change.key"
            class="bg-muted/50 rounded-lg border p-3"
          >
            <div class="mb-2 font-medium">{{ change.label }}</div>
            <div class="flex items-center gap-2 text-sm">
              <div class="bg-destructive/10 text-destructive min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap rounded px-2 py-1">
                {{ formatValue(change.oldValue) }}
              </div>
              <ArrowRight class="text-muted-foreground size-4 shrink-0" />
              <div class="min-w-0 flex-1 overflow-hidden text-ellipsis whitespace-nowrap rounded bg-green-500/10 px-2 py-1 text-green-700 dark:text-green-400">
                {{ formatValue(change.newValue) }}
              </div>
            </div>
          </div>
        </div>
      </ScrollArea>

      <AlertDialogFooter class="flex-col gap-2 sm:flex-col">
        <div class="flex w-full gap-2">
          <AlertDialogCancel as-child class="flex-1">
            <Button variant="outline" @click="handleKeepMine">
              {{ t("crud.realtime.keep_mine") }}
            </Button>
          </AlertDialogCancel>
          <AlertDialogAction as-child class="flex-1">
            <Button variant="default" @click="handleAcceptTheirs">
              {{ t("crud.realtime.accept_theirs") }}
            </Button>
          </AlertDialogAction>
        </div>
        <Button
          variant="ghost"
          class="text-muted-foreground w-full"
          @click="handleRefresh"
        >
          {{ t("crud.realtime.refresh_page") }}
        </Button>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
</template>
